import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { TDayPartingResponse } from "domain/entities/day-parting-response";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "../../../utils/files/downloadBlob";
import { checkParamsFromPortfolio } from "helper/filtersHelper";

type TDayPartingProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type DayPartingState = {
  requestParamsIndex: number;
  dayPartingData?: TDayPartingResponse;
  downloadingReports?: boolean;
  downloadDayPartingCSV: () => void;
  isLoading: boolean;
};

const initContext: DayPartingState = {
  requestParamsIndex: 0,
  downloadDayPartingCSV: () => null,
  isLoading: true,
};
export const DayPartingContext = createContext(initContext);

export const DayPartingProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: TDayPartingProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dayPartingData, setDayPartingData] = useState<TDayPartingResponse>();
  const [downloadingReports, setDownloadingReports] = useState<boolean>(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadDayPartingCSV = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportDayPartingCSV(currentRequestParams)
      .then(createBlobDownloader("users-by-time-of-day.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsFromPortfolio);
    }

    return checkParamsFromPortfolio(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getDayPartingRange(currentRequestParams)
        .then(setDayPartingData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <DayPartingContext.Provider
      value={{
        requestParamsIndex,
        dayPartingData,
        downloadingReports,
        downloadDayPartingCSV,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </DayPartingContext.Provider>
  );
};
